(() => {
  const rootSelector = '[data-bp-brand-gateway]';

  if (window.BPBrandGateway) {
    window.BPBrandGateway.initialize(document);
    return;
  }

  const instances = new WeakMap();
  const instancesBySectionId = new Map();

  class BrandGateway {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.isDesignMode = root.dataset.designMode === 'true' || Boolean(window.Shopify?.designMode);
      this.panels = Array.from(root.querySelectorAll('[data-bp-gateway-panel]'));
      this.triggers = this.panels.map((panel) => panel.querySelector('[data-bp-gateway-trigger]'));
      this.tablist = root.querySelector('[data-bp-gateway-tabs]');
      this.monogramRail = root.querySelector('[data-bp-monogram-rail]');
      this.monogramGlass = root.querySelector('[data-bp-monogram-glass]');
      this.monogramButtons = Array.from(root.querySelectorAll('[data-bp-monogram]'));
      this.desktopMedia = window.matchMedia('(min-width: 900px)');
      this.reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.abortController = new AbortController();
      this.resizeObserver = null;
      this.resizeFrame = null;
      this.monogramResizeObserver = null;
      this.monogramResizeFrame = null;
      this.monogramMotionFrame = null;
      this.monogramPosition = null;
      this.handleMonogramMotionPreference = null;
      this.unsubscribeUniverse = null;
      this.sectionSelected = false;
      this.blockSelected = false;
      this.editorPreviewApplied = false;
    }

    initialize() {
      if (!this.panels.length) return;

      this.root.dataset.bpUniverseReady = 'false';
      this.updateHeaderHeight();
      this.observeHeader();
      this.applyCapabilityMode();
      this.bindControls();
      this.bindCarouselGestures();
      this.bindViewportEvents();
      this.bindMonogramGlass();
      this.bindControllerHandshake();
      this.updateOrientation();
      this.root.classList.add('is-initialized');
      this.connectUniverseController();
      window.BPUniverse?.initialize?.(this.root);
    }

    refreshPanels() {
      this.panels = Array.from(this.root.querySelectorAll('[data-bp-gateway-panel]'));
      this.triggers = this.panels.map((panel) => panel.querySelector('[data-bp-gateway-trigger]'));
      this.monogramButtons = Array.from(this.root.querySelectorAll('[data-bp-monogram]'));
      this.scheduleMonogramReposition();
    }

    controller() {
      const api = window.BPUniverse;
      return api?.stateStoreVersion === '2' && typeof api.getState === 'function' ? api : null;
    }

    connectUniverseController() {
      const controller = this.controller();
      if (!controller) return false;

      this.unsubscribeUniverse?.();
      this.unsubscribeUniverse = controller.subscribe((state) => this.renderUniverseState(state));
      this.renderUniverseState(controller.getState());
      controller.synchronize({ source: 'gateway-connect', transition: false });
      this.emitGatewayReady(controller.getState().universe);
      return true;
    }

    renderUniverseState(state) {
      const controller = this.controller();
      if (!controller) return;
      controller.synchronizeGateway(this.root, state.universe, { announce: false });
      this.root.querySelectorAll('[data-bp-gateway-jump]').forEach((button) => {
        const panel = this.panels[Number(button.dataset.bpGatewayJump)];
        button.setAttribute('aria-pressed', String(panel?.dataset.universe === state.universe));
      });
      this.syncMonogramGlass(state.universe, {
        immediate: this.isDesignMode || state.source?.startsWith('theme-editor') ||
          state.source === 'gateway-viewport-change',
      });
      this.root.dataset.bpUniverseReady = String(controller.isReady());
    }

    bindMonogramGlass() {
      if (!this.monogramRail || !this.monogramGlass) return;
      this.handleMonogramMotionPreference = () => this.scheduleMonogramReposition();
      if (this.reducedMotionMedia.addEventListener) {
        this.reducedMotionMedia.addEventListener('change', this.handleMonogramMotionPreference);
      } else {
        this.reducedMotionMedia.addListener?.(this.handleMonogramMotionPreference);
      }
      if (typeof ResizeObserver !== 'undefined') {
        this.monogramResizeObserver = new ResizeObserver(() => this.scheduleMonogramReposition());
        this.monogramResizeObserver.observe(this.monogramRail);
      }
    }

    scheduleMonogramReposition() {
      if (!this.monogramGlass) return;
      if (this.monogramResizeFrame) window.cancelAnimationFrame(this.monogramResizeFrame);
      this.monogramResizeFrame = window.requestAnimationFrame(() => {
        this.monogramResizeFrame = null;
        this.syncMonogramGlass(this.controller()?.getState().universe || 'neutral', { immediate: true });
      });
    }

    readMonogramPosition() {
      const fallback = this.monogramPosition || { x: 0, scale: 1 };
      const computed = window.getComputedStyle(this.monogramGlass).transform;
      if (!computed || computed === 'none') return fallback;
      try {
        const Matrix = window.DOMMatrixReadOnly || window.WebKitCSSMatrix;
        if (Matrix) {
          const matrix = new Matrix(computed);
          const x = matrix.m41 ?? matrix.e;
          const scale = matrix.m11 ?? matrix.a;
          if (Number.isFinite(x) && Number.isFinite(scale)) return { x, scale };
        }
      } catch (_) {}
      const values = computed.match(/^matrix(3d)?\(([^)]+)\)$/);
      if (!values) return fallback;
      const parts = values[2].split(',').map(Number);
      const x = values[1] ? parts[12] : parts[4];
      return Number.isFinite(x) && Number.isFinite(parts[0]) ? { x, scale: parts[0] } : fallback;
    }

    writeMonogramPosition(x, scale) {
      this.monogramPosition = { x, scale };
      this.monogramGlass.style.transform = `translate3d(${x}px, 0, 0) scaleX(${scale})`;
    }

    syncMonogramGlass(universe, { immediate = false } = {}) {
      if (!this.monogramGlass || !this.monogramRail) return;
      const button = this.monogramButtons.find((item) => item.dataset.bpMonogram === universe);
      const glass = this.monogramGlass;
      if (!button) {
        if (this.monogramMotionFrame) window.cancelAnimationFrame(this.monogramMotionFrame);
        this.monogramMotionFrame = null;
        glass.hidden = true;
        glass.dataset.bpGlassCurrent = 'neutral';
        glass.dataset.bpGlassTarget = 'neutral';
        glass.dataset.bpGlassMotion = 'idle';
        this.monogramPosition = null;
        return;
      }

      glass.hidden = false;
      const targetX = button.offsetLeft + (button.offsetWidth - glass.offsetWidth) / 2;
      const motionDisabled = immediate || this.reducedMotionMedia.matches ||
        this.root.dataset.animationsEnabled === 'false' || this.root.classList.contains('is-reduced-effects') ||
        this.root.classList.contains('is-editor-paused') || typeof window.requestAnimationFrame !== 'function';
      if (motionDisabled || !this.monogramPosition) {
        if (this.monogramMotionFrame) window.cancelAnimationFrame(this.monogramMotionFrame);
        this.monogramMotionFrame = null;
        this.writeMonogramPosition(targetX, 1);
        glass.dataset.bpGlassCurrent = universe;
        glass.dataset.bpGlassTarget = universe;
        glass.dataset.bpGlassMotion = 'idle';
        return;
      }
      if (this.monogramMotionFrame && glass.dataset.bpGlassTarget === universe) return;
      const from = this.readMonogramPosition();
      if (this.monogramMotionFrame) window.cancelAnimationFrame(this.monogramMotionFrame);
      this.monogramMotionFrame = null;
      const distance = targetX - from.x;
      if (Math.abs(distance) < .5 && Math.abs(from.scale - 1) < .01) {
        this.writeMonogramPosition(targetX, 1);
        glass.dataset.bpGlassCurrent = universe;
        glass.dataset.bpGlassTarget = universe;
        glass.dataset.bpGlassMotion = 'idle';
        return;
      }

      glass.dataset.bpGlassTarget = universe;
      glass.dataset.bpGlassMotion = 'moving';
      const smootherstep = (value) => value * value * value * (value * (value * 6 - 15) + 10);
      const bump = (value, start, end) => {
        if (value <= start || value >= end) return 0;
        const wave = Math.sin(Math.PI * (value - start) / (end - start));
        return wave * wave;
      };
      const compression = Math.min(.18, Math.max(0, from.scale - .72));
      const start = window.performance.now();
      const frame = (now) => {
        const progress = Math.min(1, (now - start) / 620);
        const eased = smootherstep(progress);
        this.writeMonogramPosition(
          from.x + distance * eased,
          from.scale + (1 - from.scale) * eased - compression * bump(progress, 0, .34) +
            .10 * bump(progress, .26, .80) - .04 * bump(progress, .72, 1),
        );
        if (progress < 1) {
          this.monogramMotionFrame = window.requestAnimationFrame(frame);
        } else {
          this.monogramMotionFrame = null;
          glass.dataset.bpGlassCurrent = universe;
          glass.dataset.bpGlassMotion = 'idle';
        }
      };
      this.writeMonogramPosition(from.x, from.scale);
      this.monogramMotionFrame = window.requestAnimationFrame(frame);
    }

    bindControllerHandshake() {
      window.addEventListener(
        'bp:universe-controller-ready',
        () => this.connectUniverseController(),
        { signal: this.abortController.signal },
      );
    }

    bindControls() {
      const { signal } = this.abortController;

      this.root.querySelectorAll('[data-bp-gateway-jump]').forEach((button) => {
        button.addEventListener('click', () => {
          this.activatePanel(Number(button.dataset.bpGatewayJump), true, false);
        }, { signal });
      });

      this.triggers.forEach((trigger, index) => {
        if (!trigger) return;

        trigger.addEventListener('click', () => this.activatePanel(index, true), { signal });
        // Pointer focus precedes click: starting a capture here can retarget that click.
        // Keyboard focus remains an immediate, non-animated selection.
        trigger.addEventListener('focus', () => {
          if (trigger.matches(':focus-visible') &&
              this.controller()?.getState().universe !== this.panels[index]?.dataset.universe) {
            this.activatePanel(index, true, false);
          }
        }, { signal });
        trigger.addEventListener('keydown', (event) => this.handleTriggerKeydown(event, index), { signal });
      });

      this.panels.forEach((panel, index) => {
        panel.addEventListener('click', (event) => {
          if (event.defaultPrevented || event.button > 0 || !(event.target instanceof Element)) return;
          if (event.target.closest('a, button, input, select, textarea, label, summary, [role="button"], [role="link"], [contenteditable], [aria-disabled="true"]')) return;
          if (event.target.closest('[data-bp-gateway-panel]') !== panel) return;
          this.activatePanel(index, true);
        }, { signal });
      });
    }

    bindViewportEvents() {
      const { signal } = this.abortController;
      const handleViewportChange = () => {
        this.scheduleHeaderMeasurement();
        this.scheduleMonogramReposition();
        this.updateOrientation();
        this.controller()?.synchronize({ source: 'gateway-viewport-change', transition: false });
      };

      window.addEventListener('resize', handleViewportChange, { passive: true, signal });
      window.addEventListener('orientationchange', handleViewportChange, { passive: true, signal });
      this.desktopMedia.addEventListener('change', handleViewportChange, { signal });
    }

    configuredInitialUniverse() {
      if (this.root.dataset.homepageInitial) return this.root.dataset.homepageInitial;
      if (!this.desktopMedia.matches) return this.root.dataset.mobileInitial;
      return this.root.dataset.desktopState === 'active' ? this.root.dataset.defaultUniverse : 'neutral';
    }

    handleTriggerKeydown(event, currentIndex) {
      const visibleIndexes = this.panels.map((panel, index) => panel.hidden ? null : index).filter((index) => index !== null);
      const currentPosition = visibleIndexes.indexOf(currentIndex);
      if (currentPosition < 0) return;
      const lastPosition = visibleIndexes.length - 1;
      let nextPosition = null;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextPosition = currentPosition === lastPosition ? 0 : currentPosition + 1;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextPosition = currentPosition === 0 ? lastPosition : currentPosition - 1;
          break;
        case 'Home':
          nextPosition = 0;
          break;
        case 'End':
          nextPosition = lastPosition;
          break;
        default:
          return;
      }

      event.preventDefault();
      const nextIndex = visibleIndexes[nextPosition];
      this.activatePanel(nextIndex, true, false);
      this.triggers[nextIndex]?.focus();
    }

    bindCarouselGestures() {
      if (this.root.dataset.bpCarousel === undefined || !this.tablist) return;
      const { signal } = this.abortController;
      let gesture = null;
      let consumedClick = false;
      this.tablist.addEventListener('pointerdown', (event) => {
        consumedClick = false;
        gesture = null;
        if (!event.isPrimary || event.button !== 0 || event.pointerType === 'mouse' ||
            !event.target.closest('.bp-brand-gateway__visual')) return;
        gesture = { id: event.pointerId, x: event.clientX, y: event.clientY,
          universe: this.controller()?.getState().universe };
      }, { passive: true, signal });
      this.tablist.addEventListener('pointerup', (event) => {
        if (!gesture || gesture.id !== event.pointerId) return;
        const start = gesture;
        gesture = null;
        const dx = event.clientX - start.x, dy = event.clientY - start.y;
        if (Math.abs(dx) < 36 || Math.abs(dx) <= Math.abs(dy) * 1.4 ||
            start.universe !== this.controller()?.getState().universe) return;
        const visiblePanels = this.panels.filter((panel) => !panel.hidden);
        const index = visiblePanels.findIndex(panel => panel.dataset.universe === start.universe);
        if (index < 0) return;
        consumedClick = true;
        const target = visiblePanels[(index + (dx < 0 ? 1 : visiblePanels.length - 1)) % visiblePanels.length];
        this.activatePanel(this.panels.indexOf(target), true);
      }, { passive: true, signal });
      this.tablist.addEventListener('pointercancel', () => { gesture = null; }, { passive: true, signal });
      window.addEventListener('blur', () => { gesture = null; }, { signal });
      window.addEventListener('resize', () => { gesture = null; }, { passive: true, signal });
      this.tablist.addEventListener('click', (event) => {
        if (!consumedClick || event.detail === 0) return;
        consumedClick = false;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, { capture: true, signal });
    }

    activatePanel(index, fromUser, transition = true) {
      const universe = this.panels[index]?.hidden ? null : this.panels[index]?.dataset.universe;
      const controller = this.controller();
      if (!universe || !controller) return;

      const source = this.isDesignMode ? 'theme-editor' : 'brand-gateway';
      controller.setState(universe, {
        announce: fromUser,
        persist: fromUser && !this.isDesignMode,
        source,
        transition,
      });

      if (fromUser && this.root.dataset.bpEditorialGateway !== undefined && !this.desktopMedia.matches &&
          this.tablist && this.tablist.scrollWidth > this.tablist.clientWidth + 1) {
        this.panels[index].scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'instant' });
      }

      if (fromUser && !this.isDesignMode) this.emitUniverseChange(universe);
    }

    emitUniverseChange(universe) {
      this.root.dispatchEvent(
        new CustomEvent('bp:universe-change', {
          bubbles: true,
          detail: {
            universe,
            source: 'brand-gateway',
            sectionId: this.sectionId,
            stateApplied: true,
            timestamp: new Date().toISOString(),
          },
        }),
      );
    }

    emitGatewayReady(universe) {
      this.root.dispatchEvent(
        new CustomEvent('bp:gateway-ready', {
          bubbles: true,
          detail: {
            universe: universe || 'neutral',
            source: 'brand-gateway',
            sectionId: this.sectionId,
            timestamp: new Date().toISOString(),
          },
        }),
      );
    }

    updateOrientation() {
      if (this.root.dataset.bpEditorialGateway !== undefined) return;
      this.tablist?.setAttribute('aria-orientation', this.desktopMedia.matches ? 'horizontal' : 'vertical');
    }

    applyCapabilityMode() {
      if (this.root.dataset.autoReduceEffects !== 'true') return;

      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const lowMemory = typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 4;
      const lowConcurrency = typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency <= 4;
      const saveData = Boolean(connection?.saveData);

      this.root.classList.toggle('is-reduced-effects', lowMemory || lowConcurrency || saveData);
    }

    findHeader() {
      return document.querySelector('.page-header') || document.querySelector('header');
    }

    updateHeaderHeight() {
      const nativeValue = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue('--header-natural-height')
        .trim();
      const nativeHeight = Number.parseFloat(nativeValue);
      const header = this.findHeader();
      const measuredHeight = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
      const effectiveHeight = Number.isFinite(nativeHeight) && nativeHeight > 0 ? nativeHeight : measuredHeight;

      this.root.style.setProperty('--bp-gateway-measured-header-height', `${measuredHeight}px`);
      this.root.style.setProperty('--bp-gateway-header-height', `${Math.max(0, effectiveHeight)}px`);
    }

    scheduleHeaderMeasurement() {
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.updateHeaderHeight();
      });
    }

    observeHeader() {
      const header = this.findHeader();
      if (!header || typeof ResizeObserver === 'undefined') return;

      this.resizeObserver = new ResizeObserver(() => this.scheduleHeaderMeasurement());
      this.resizeObserver.observe(header);
    }

    handleSectionSelect() {
      this.sectionSelected = true;
      this.updateEditorPause();

      const controller = this.controller();
      if (controller?.getState().universe === 'neutral') {
        this.editorPreviewApplied = true;
        controller.setState(this.configuredInitialUniverse() || this.root.dataset.defaultUniverse, {
          source: 'theme-editor-section',
          persist: false,
          transition: false,
        });
      }
      this.root.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    }

    handleSectionDeselect() {
      this.sectionSelected = false;
      this.updateEditorPause();
      this.restoreEditorPreview();
    }

    handleBlockSelect(blockId) {
      const index = this.panels.findIndex((panel) => panel.dataset.blockId === blockId);
      if (index < 0) return;

      this.blockSelected = true;
      this.editorPreviewApplied = true;
      this.updateEditorPause();
      this.controller()?.setState(this.panels[index].dataset.universe, {
        announce: true,
        persist: false,
        source: 'theme-editor-block',
        transition: false,
      });
      this.panels[index].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    }

    handleBlockDeselect() {
      this.blockSelected = false;
      this.updateEditorPause();
      this.restoreEditorPreview();
    }

    restoreEditorPreview() {
      if (!this.editorPreviewApplied || this.sectionSelected || this.blockSelected) return;
      this.editorPreviewApplied = false;
      this.controller()?.setState(this.controller().resolveInitialUniverse(this.configuredInitialUniverse()), {
        source: 'theme-editor',
        persist: false,
        transition: false,
      });
    }

    updateEditorPause() {
      this.root.classList.toggle('is-editor-paused', this.sectionSelected || this.blockSelected);
    }

    destroy() {
      this.unsubscribeUniverse?.();
      this.unsubscribeUniverse = null;
      this.abortController.abort();
      this.resizeObserver?.disconnect();
      this.monogramResizeObserver?.disconnect();
      if (this.handleMonogramMotionPreference) {
        if (this.reducedMotionMedia.removeEventListener) {
          this.reducedMotionMedia.removeEventListener('change', this.handleMonogramMotionPreference);
        } else {
          this.reducedMotionMedia.removeListener?.(this.handleMonogramMotionPreference);
        }
      }
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
      if (this.monogramResizeFrame) window.cancelAnimationFrame(this.monogramResizeFrame);
      if (this.monogramMotionFrame) window.cancelAnimationFrame(this.monogramMotionFrame);
      window.BPUniverse?.destroy?.(this.root);

      this.root.classList.remove('is-initialized', 'has-active-panel', 'is-editor-paused');
      this.root.dataset.bpUniverseReady = 'false';
      instances.delete(this.root);
      instancesBySectionId.delete(this.sectionId);
    }
  }

  const rootsWithin = (container) => {
    const roots = [];
    if (container instanceof Element && container.matches(rootSelector)) roots.push(container);
    container.querySelectorAll?.(rootSelector).forEach((root) => roots.push(root));
    return roots;
  };

  const initialize = (container = document) => {
    rootsWithin(container).forEach((root) => {
      if (instances.has(root)) return;

      const instance = new BrandGateway(root);
      instances.set(root, instance);
      instancesBySectionId.set(instance.sectionId, instance);
      instance.initialize();
    });
  };

  const destroy = (container) => {
    rootsWithin(container).forEach((root) => instances.get(root)?.destroy());
  };

  const instanceForEvent = (event) => {
    const directRoot = event.target?.closest?.(rootSelector) || event.target?.querySelector?.(rootSelector);
    if (directRoot && instances.has(directRoot)) return instances.get(directRoot);

    const sectionId = event.detail?.sectionId;
    return sectionId ? instancesBySectionId.get(sectionId) : null;
  };

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:section:select', (event) => instanceForEvent(event)?.handleSectionSelect());
  document.addEventListener('shopify:section:deselect', (event) => instanceForEvent(event)?.handleSectionDeselect());
  document.addEventListener('shopify:block:select', (event) => instanceForEvent(event)?.handleBlockSelect(event.detail?.blockId));
  document.addEventListener('shopify:block:deselect', (event) => instanceForEvent(event)?.handleBlockDeselect());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }

  const refresh = (root) => instances.get(root)?.refreshPanels();
  window.BPBrandGateway = Object.freeze({ initialize, destroy, refresh });
})();
